import { configureStore } from '@reduxjs/toolkit';
import { authApi } from '../redux/auth/authApi';
import { userApi } from '../redux/user/userApi';
import { handoverTaskApi } from '../redux/task/handoverTaskApi';
import { handoverApi } from '../redux/handover/handoverApi';
import { handoverAccessApi } from '../redux/access/handoverAccessApi';
import { handoverCommentApi } from '../redux/comment/commentApi';
import { aiApi } from '../redux/ai/aiApi';
import { healthApi } from '../redux/health/healthApi';
import { departureApi } from '../redux/departure/departureApi';
import { incomingViewApi } from '../redux/incomingView/incomingViewApi';
import { managerDashboardApi } from '../redux/managerDashboard/managerDashboardApi';
import { auditLogApi } from '../redux/auditLog/auditLogApi';
import { customerServiceNotificationsApi } from '../redux/customerServiceNotifications/customerServiceNotificationsApi';
import { companyApi } from '../redux/company/companyApi';
import authReducer from '../redux/auth/authSlice';
import handoverReducer from '../redux/handover/handoverSlice';
import userReducer from '../redux/user/userSlice';
import handoverTaskReducer from '../redux/task/handoverTaskSlice';
import handoverAccessReducer from '../redux/access/handoverAccessSlice';
import handoverCommentReducer from '../redux/comment/commentSlice';
import aiReducer from '../redux/ai/aiSlice';
import healthReducer from '../redux/health/healthSlice';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import {
  persistAuthConfig,
  persistUserConfig,
  persistHandoverConfig,
  persistHandoverTaskConfig,
  persistHandoverCommentConfig,
} from './persistConfigs';

export {
  persistAuthConfig,
  persistUserConfig,
  persistHandoverConfig,
  persistHandoverTaskConfig,
  persistHandoverCommentConfig,
};

// Wrap the reducers with persistReducer
const persistedAuthReducer = persistReducer(persistAuthConfig, authReducer);
const persistedUserReducer = persistReducer(persistUserConfig, userReducer);
const persistedHandoverReducer = persistReducer(persistHandoverConfig, handoverReducer);
const persistedHandoverTaskReducer = persistReducer(persistHandoverTaskConfig, handoverTaskReducer);
const persistedHandoverCommentReducer = persistReducer(persistHandoverCommentConfig, handoverCommentReducer);

export const store = configureStore({
  reducer: {
    auth: persistedAuthReducer, // Persisted auth reducer
    user: persistedUserReducer, // Persisted user reducer
    handover: persistedHandoverReducer, // Persisted handover reducer
    handoverTask: persistedHandoverTaskReducer, // Persisted handoverTask reducer
    ai: aiReducer, // ai reducer
    health: healthReducer, // health reducer for feature flags
    handoverAccess: handoverAccessReducer, // handoverAccess reducer here
    comments: persistedHandoverCommentReducer, // Add comment reducer here
    [authApi.reducerPath]: authApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [handoverApi.reducerPath]: handoverApi.reducer,
    [handoverTaskApi.reducerPath]: handoverTaskApi.reducer,
    [handoverAccessApi.reducerPath]: handoverAccessApi.reducer, // handoverAccessApi reducer
    [handoverCommentApi.reducerPath]: handoverCommentApi.reducer,
    [aiApi.reducerPath]: aiApi.reducer,
    [healthApi.reducerPath]: healthApi.reducer,
    [departureApi.reducerPath]: departureApi.reducer,
    [incomingViewApi.reducerPath]: incomingViewApi.reducer,
    [managerDashboardApi.reducerPath]: managerDashboardApi.reducer,
    [auditLogApi.reducerPath]: auditLogApi.reducer,
    [customerServiceNotificationsApi.reducerPath]: customerServiceNotificationsApi.reducer,
    [companyApi.reducerPath]: companyApi.reducer,
  },
  devTools: process.env.NODE_ENV === 'development',
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(
      authApi.middleware,
      userApi.middleware,
      handoverApi.middleware,
      handoverTaskApi.middleware,
      handoverAccessApi.middleware,
      handoverCommentApi.middleware,
      aiApi.middleware,
      healthApi.middleware,
      departureApi.middleware,
      incomingViewApi.middleware,
      managerDashboardApi.middleware,
      auditLogApi.middleware,
      customerServiceNotificationsApi.middleware,
      companyApi.middleware
    ),
});

// Create the persistor for persisting the store
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
